// ─────────────────────────────────────────────────────────────────────────────
// useWidgetRegistry – React hook backed by useSyncExternalStore
// Re-renders the consumer when registered widgets change.
// ─────────────────────────────────────────────────────────────────────────────

import { useSyncExternalStore } from "react";
import { widgetRegistry } from "./widgetRegistry";
import type { RegisteredWidget, WidgetIndexEntry } from "./types";

let listCacheVersion = 0;
let listCache: RegisteredWidget[] = [];
let lastWidgetEventToken = "";

function bumpVersionOnEvent(): void {
	listCacheVersion++;
}

const initialUnsub = widgetRegistry.subscribe(bumpVersionOnEvent);
// Keep the subscription alive forever — consumers are tied to React lifecycle,
// but this background subscriber is what invalidates the cache.
void initialUnsub;

function subscribe(fn: () => void): () => void {
	return widgetRegistry.subscribe(fn);
}

function getSnapshot(): RegisteredWidget[] {
	const token = `${listCacheVersion}|${widgetRegistry.list().length}`;
	if (token !== lastWidgetEventToken) {
		listCache = widgetRegistry.list();
		lastWidgetEventToken = token;
	}
	return listCache;
}

function getServerSnapshot(): RegisteredWidget[] {
	return [];
}

export function useWidgetRegistry(): RegisteredWidget[] {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

let indexCache: WidgetIndexEntry[] = [];
let lastIndexToken = "";

function getIndexSnapshot(): WidgetIndexEntry[] {
	const token = `${listCacheVersion}|${widgetRegistry.list().length}`;
	if (token !== lastIndexToken) {
		indexCache = widgetRegistry.getIndex();
		lastIndexToken = token;
	}
	return indexCache;
}

export function useWidgetIndex(): WidgetIndexEntry[] {
	return useSyncExternalStore(subscribe, getIndexSnapshot, getServerSnapshot);
}
